/**
 * Inventory Service - Fetches inventory data from Supabase
 * 
 * Handles data retrieval from:
 * - inventory_items: SKU catalog with item details
 * - stock_levels: Current inventory quantities and status
 */

import { supabase } from "@/lib/supabase";
import { isUuid } from "@/lib/uuid";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { StockLevel, StockStatus } from "@/data/inventoryAdapter";
import type { Job, MaterialLineItem } from "@/data/types";
import { SystemUpdateService } from '@/services/system-update.service';

export interface InventoryItem {
  id: string;
  sku: string;
  item_name: string;
  category: string | null;
  vendor: string | null;
  unit_of_measure: string | null;
  cost: number | null;
  price: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StockLevelRecord {
  id: string;
  sku: string;
  quantity_on_hand: number;
  quantity_reserved: number;
  reorder_point: number;
  status: string | null;
  last_synced_at: string | null;
  source: string | null;
  updated_at: string;
}

export interface WarehouseStockRecord {
  id: string;
  sku: string;
  product_name: string;
  current_stock: number;
  reserved_stock: number;
  incoming_stock: number;
  available_stock: number;
  reorder_threshold: number;
  created_at: string;
  updated_at: string;
}

const INVENTORY_LIST_CACHE_TTL_MS = 15_000;
const STOCK_LEVEL_CACHE_TTL_MS = 15_000;
const PRODUCT_REGISTRY_SKU_CACHE_TTL_MS = 15_000;
const ENABLE_METADATA_MATERIALS_FALLBACK = (import.meta.env.VITE_ENABLE_METADATA_MATERIALS_FALLBACK ?? "true") === "true";
let inventoryListCache: { at: number; data: StockLevel[] } | null = null;
let inventoryListInFlight: Promise<StockLevel[]> | null = null;
const stockLevelCache = new Map<string, { at: number; data: StockLevel | null }>();
const stockLevelInFlight = new Map<string, Promise<StockLevel | null>>();
const inventoryItemNameCache = new Map<string, string>();
const inventoryItemNameInFlight = new Map<string, Promise<string>>();
const inventoryItemExistsCache = new Set<string>();
const inventoryItemExistsInFlight = new Map<string, Promise<void>>();
const stockLevelRecordInFlight = new Map<string, Promise<StockLevelRecord | null>>();
const warehouseStockRecordInFlight = new Map<string, Promise<WarehouseStockRecord | null>>();
const stockLevelSyncInFlight = new Map<string, Promise<void>>();
const warehouseStockSyncInFlight = new Map<string, Promise<void>>();
let jobMaterialsSnapshotSupport:
  | {
      canUseProductId: boolean;
      canUsePricesSnapshot: boolean;
      canUseUnitCostSnapshot: boolean;
      canUseUnitPriceSnapshot: boolean;
    }
  | null = null;
let jobMaterialsSnapshotSupportInFlight:
  | Promise<{
      canUseProductId: boolean;
      canUsePricesSnapshot: boolean;
      canUseUnitCostSnapshot: boolean;
      canUseUnitPriceSnapshot: boolean;
    }>
  | null = null;
let productRegistrySkuCache: { at: number; data: Map<string, string> } | null = null;
let productRegistrySkuInFlight: Promise<Map<string, string>> | null = null;

function getStockMutationSignature(options: StockMutationOptions): string {
  return [
    Number(options.quantityOnHandDelta ?? 0),
    Number(options.quantityReservedDelta ?? 0),
    Number(options.incomingStockDelta ?? 0),
  ].join("|");
}

async function withStockSyncDeduplication(
  syncMap: Map<string, Promise<void>>,
  syncKey: string,
  run: () => Promise<void>,
): Promise<void> {
  const inFlight = syncMap.get(syncKey);
  if (inFlight) return inFlight;

  const promise = run();
  syncMap.set(syncKey, promise);
  try {
    await promise;
  } finally {
    if (syncMap.get(syncKey) === promise) {
      syncMap.delete(syncKey);
    }
  }
}

/**
 * Fetch all active inventory items with their stock levels
 * Returns data in the format expected by InventoryAdapter
 */
export async function fetchInventoryData(): Promise<StockLevel[]> {
  const now = Date.now();
  if (inventoryListCache && now - inventoryListCache.at < INVENTORY_LIST_CACHE_TTL_MS) {
    return inventoryListCache.data;
  }
  if (inventoryListInFlight) return inventoryListInFlight;

  const promise = (async () => {
  // First, get all stock levels with their associated inventory items
  const { data: stockLevels, error: stockError } = await supabase
    .from("stock_levels")
    .select(`
      sku,
      quantity_on_hand,
      quantity_reserved,
      reorder_point,
      status,
      last_synced_at,
      source,
      updated_at,
      inventory_items (
        item_name
      )
    `)
    .order("sku", { ascending: true });

  if (stockError) {
    console.error("[inventory.service] Error fetching stock levels:", stockError.message, stockError.code);
    return [];
  }

  // If no data in stock_levels, fall back to inventory_items only
  if (!stockLevels || stockLevels.length === 0) {
    const { data: items, error: itemsError } = await supabase
      .from("inventory_items")
      .select("*")
      .eq("is_active", true)
      .order("sku", { ascending: true });

    if (itemsError) {
      console.error("Error fetching inventory items:", itemsError);
      throw itemsError;
    }

    // Transform to StockLevel format
    return (items || []).map((item) => ({
      sku: item.sku,
      itemName: item.item_name,
      quantityOnHand: 0,
      quantityReserved: 0,
      quantityAvailable: 0,
      reorderPoint: 0,
      status: "In Stock" as StockStatus,
      lastSyncedAt: new Date().toISOString(),
      source: "boxhero" as const,
    }));
  }

   // Transform the joined data to StockLevel format
   const transformed: StockLevel[] = stockLevels
     .filter((record) => record.inventory_items !== null)
     .map((record) => {
       const quantityOnHand = record.quantity_on_hand ?? 0;
       const quantityReserved = record.quantity_reserved ?? 0;
       const quantityAvailable = Math.max(0, quantityOnHand - quantityReserved);
       const reorderPoint = record.reorder_point ?? 0;
       
       // Determine status based on available quantity vs reorder point
       let status: StockStatus = "In Stock";
       if (quantityAvailable <= 0) {
         status = "Out of Stock";
       } else if (quantityAvailable <= reorderPoint) {
         status = "Low Stock";
       } else if (record.status) {
         // Use database status if available
         status = record.status as StockStatus;
       }
 
       return {
         sku: record.sku,
         itemName: (Array.isArray(record.inventory_items) ? record.inventory_items[0]?.item_name : (record.inventory_items as { item_name?: string } | null)?.item_name) || record.sku,
         quantityOnHand,
         quantityReserved,
         quantityAvailable,
         reorderPoint,
         status,
         lastSyncedAt: record.last_synced_at || new Date().toISOString(),
         source: ((record.source as string) === "boxhero" ? "boxhero" : "simulated") as "boxhero" | "simulated",
       };
     });

  return transformed;
  })();

  inventoryListInFlight = promise;
  try {
    const result = await promise;
    inventoryListCache = { at: Date.now(), data: result };
    return result;
  } finally {
    inventoryListInFlight = null;
  }
}

/**
 * Fetch single SKU stock level
 */
export async function fetchStockLevel(sku: string): Promise<StockLevel | null> {
  const key = String(sku).trim();
  const now = Date.now();
  const cached = stockLevelCache.get(key);
  if (cached && now - cached.at < STOCK_LEVEL_CACHE_TTL_MS) return cached.data;
  const inFlight = stockLevelInFlight.get(key);
  if (inFlight) return inFlight;

  const promise = (async () => {
  const { data: record, error } = await supabase
    .from("stock_levels")
    .select(`
      sku,
      quantity_on_hand,
      quantity_reserved,
      reorder_point,
      status,
      last_synced_at,
      source,
      updated_at,
      inventory_items (
        item_name
      )
    `)
    .eq("sku", sku)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // No record found - check if item exists
      const { data: item } = await supabase
        .from("inventory_items")
        .select("item_name")
        .eq("sku", sku)
        .single();

      if (item) {
        const fallback = {
          sku,
          itemName: item.item_name,
          quantityOnHand: 0,
          quantityReserved: 0,
          quantityAvailable: 0,
          reorderPoint: 0,
          status: "In Stock" as StockStatus,
          lastSyncedAt: new Date().toISOString(),
          source: "simulated" as const,
        };
        stockLevelCache.set(key, { at: Date.now(), data: fallback });
        return fallback;
      }
    }
    console.error("Error fetching stock level:", error);
    stockLevelCache.set(key, { at: Date.now(), data: null });
    return null;
  }

  const quantityOnHand = record.quantity_on_hand ?? 0;
  const quantityReserved = record.quantity_reserved ?? 0;
  const quantityAvailable = Math.max(0, quantityOnHand - quantityReserved);
  const reorderPoint = record.reorder_point ?? 0;

  let status: StockStatus = "In Stock";
  if (quantityAvailable <= 0) {
    status = "Out of Stock";
  } else if (quantityAvailable <= reorderPoint) {
    status = "Low Stock";
  }

  const resolved = {
    sku: record.sku,
    itemName: (record.inventory_items as any)?.[0]?.item_name || record.sku,
    quantityOnHand,
    quantityReserved,
    quantityAvailable,
    reorderPoint,
    status,
    lastSyncedAt: record.last_synced_at || new Date().toISOString(),
    source: (record.source as "simulated" | "boxhero") || "simulated",
  };
  stockLevelCache.set(key, { at: Date.now(), data: resolved });
  return resolved;
  })();

  stockLevelInFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    stockLevelInFlight.delete(key);
  }
}

/**
 * Check if Supabase connection is available
 */
export async function checkSupabaseConnection(): Promise<boolean> {
  try {
    const { error } = await supabase.from("inventory_items").select("id").limit(1);
    return !error;
  } catch {
    return false;
  }
}

/* ══════════════════════════════════════════════════════════
   Job Materials Data (for Job Materials tab)
   ══════════════════════════════════════════════════════════ */

export interface JobMaterialRecord {
  id: string;
  jobId: string;
  sku: string;
  itemName: string;
  quantityRequired: number;
  quantityAllocated: number;
  quantityStaged: number;
  quantityInstalled: number;
  missingItems: number;
  fieldPurchase: boolean;
  inventoryAssignment: "Assigned" | "Stock";
  lifecycleStage: "Ordered" | "Received" | "Assigned" | "Installed";
  inventoryStatus: "In Stock" | "Reserved" | "Allocated" | "Low Stock";
  customerName?: string;
}

export interface JobWithMaterials {
  id: string;
  customerName: string;
  currentStage: string;
  materialsList: JobMaterialRecord[];
}

interface StockMutationOptions {
  quantityOnHandDelta?: number;
  quantityReservedDelta?: number;
  incomingStockDelta?: number;
}

interface JobMaterialMutationOptions {
  quantityAllocatedDelta?: number;
  quantityStagedDelta?: number;
  quantityInstalledDelta?: number;
  lifecycleStage?: JobMaterialRecord["lifecycleStage"];
  inventoryStatus?: JobMaterialRecord["inventoryStatus"];
}

async function getJobMaterialsSnapshotSupport(): Promise<{
  canUseProductId: boolean;
  canUsePricesSnapshot: boolean;
  canUseUnitCostSnapshot: boolean;
  canUseUnitPriceSnapshot: boolean;
}> {
  if (jobMaterialsSnapshotSupport) return jobMaterialsSnapshotSupport;
  if (jobMaterialsSnapshotSupportInFlight) return jobMaterialsSnapshotSupportInFlight;

  const promise = (async () => {
    const [productIdProbe, pricesSnapshotProbe, unitCostSnapshotProbe, unitPriceSnapshotProbe] = await Promise.all([
      supabase.from("job_materials").select("product_id").limit(1),
      supabase.from("job_materials").select("prices_snapshot").limit(1),
      supabase.from("job_materials").select("unit_cost_snapshot").limit(1),
      supabase.from("job_materials").select("unit_price_snapshot").limit(1),
    ]);

    return {
      canUseProductId: productIdProbe.error?.code !== "42703",
      canUsePricesSnapshot: pricesSnapshotProbe.error?.code !== "42703",
      canUseUnitCostSnapshot: unitCostSnapshotProbe.error?.code !== "42703",
      canUseUnitPriceSnapshot: unitPriceSnapshotProbe.error?.code !== "42703",
    };
  })();

  jobMaterialsSnapshotSupportInFlight = promise;
  try {
    const resolved = await promise;
    jobMaterialsSnapshotSupport = resolved;
    return resolved;
  } finally {
    jobMaterialsSnapshotSupportInFlight = null;
  }
}

async function getSkuToProductIdMap(): Promise<Map<string, string>> {
  const now = Date.now();
  if (productRegistrySkuCache && now - productRegistrySkuCache.at < PRODUCT_REGISTRY_SKU_CACHE_TTL_MS) {
    return productRegistrySkuCache.data;
  }
  if (productRegistrySkuInFlight) return productRegistrySkuInFlight;

  const promise = (async () => {
    const next = new Map<string, string>();
    const { data: productRows, error } = await supabase
      .from("product_registry")
      .select("sku, product_id");

    if (error) {
      console.warn("[inventory.service] Failed to prefetch product_registry IDs for materials snapshot:", error);
      return next;
    }

    (productRows ?? []).forEach((row: { sku?: string | null; product_id?: string | null }) => {
      const key = normalizeSkuKey(row.sku);
      const pid = String(row.product_id ?? "").trim();
      if (key && isUuid(pid)) next.set(key, pid);
    });

    return next;
  })();

  productRegistrySkuInFlight = promise;
  try {
    const resolved = await promise;
    productRegistrySkuCache = { at: Date.now(), data: resolved };
    return resolved;
  } finally {
    productRegistrySkuInFlight = null;
  }
}

function normalizeSkuKey(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");
}

function toNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeAssignment(value: MaterialLineItem["inventoryAssignment"]): "Assigned" | "Stock" {
  return value === "Assigned" ? "Assigned" : "Stock";
}

function normalizeLifecycle(value: MaterialLineItem["lifecycleStage"]): "Ordered" | "Received" | "Assigned" | "Installed" {
  if (value === "Received" || value === "Assigned" || value === "Installed") return value;
  return "Ordered";
}

function normalizeInventoryStatus(value: MaterialLineItem["inventoryStatus"]): "In Stock" | "Reserved" | "Allocated" | "Low Stock" {
  if (value === "Reserved" || value === "Allocated" || value === "Low Stock") return value;
  return "In Stock";
}

type RebuildJobRow = {
  id: string;
  metadata?: unknown;
  material_readiness?: unknown;
};

type RebuildJobMaterialRow = {
  job_id?: unknown;
  sku?: unknown;
  item_name?: unknown;
  quantity_required?: unknown;
  quantity_allocated?: unknown;
  quantity_staged?: unknown;
  quantity_installed?: unknown;
  field_purchase?: unknown;
  inventory_assignment?: unknown;
  lifecycle_stage?: unknown;
  inventory_status?: unknown;
};

type RebuildAllocationRow = {
  job_id?: unknown;
  sku?: unknown;
  product_name?: unknown;
  quantity?: unknown;
  status?: unknown;
};

type RebuildReceivingRow = {
  job_id?: unknown;
  sku?: unknown;
  product_name?: unknown;
  expected_quantity?: unknown;
  received_quantity?: unknown;
  status?: unknown;
};

type CanonicalMaterialSummaryRow = {
  sku: string;
  itemName: string;
  quantityRequired: number;
  quantityAllocated: number;
  quantityStaged: number;
  quantityInstalled: number;
  missingItems: number;
  fieldPurchase: boolean;
  inventoryAssignment: "Assigned" | "Stock";
  lifecycleStage: "Ordered" | "Received" | "Assigned" | "Installed";
  inventoryStatus: "In Stock" | "Reserved" | "Allocated" | "Low Stock";
};

const JOB_MATERIALS_BACKFILL_CHUNK_SIZE = 200;
const rebuildJobMaterialsInFlight = new Map<string, Promise<boolean>>();
let backfillJobMaterialsInFlight: Promise<{ total: number; updated: number; failed: number }> | null = null;

function buildJobMaterialsMetadataPatch(
  jobRow: RebuildJobRow,
  jobMaterialsRows: RebuildJobMaterialRow[],
  allocationRows: RebuildAllocationRow[],
  receivingRows: RebuildReceivingRow[],
): Record<string, unknown> | null {
  const metadata = (jobRow.metadata && typeof jobRow.metadata === "object")
    ? (jobRow.metadata as Record<string, unknown>)
    : {};
  const terminalReadiness = ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(
    String(jobRow.material_readiness ?? "").trim(),
  );
  const existingRows = ENABLE_METADATA_MATERIALS_FALLBACK && Array.isArray(metadata.materialsList)
    ? metadata.materialsList
    : [];

  const bySku = new Map<string, CanonicalMaterialSummaryRow>();
  // Tracks authoritative job_materials rows so receiving.expected cannot inflate
  // required quantities from duplicate or stale receiving records.
  const skusFromJobMaterials = new Set<string>();

  const ensure = (skuRaw: unknown, nameRaw: unknown): CanonicalMaterialSummaryRow | null => {
    const sku = String(skuRaw ?? "").trim();
    const skuKey = normalizeSkuKey(sku);
    if (!sku || !skuKey) return null;
    const existing = bySku.get(skuKey);
    if (existing) return existing;
    const next: CanonicalMaterialSummaryRow = {
      sku,
      itemName: String(nameRaw ?? sku).trim() || sku,
      quantityRequired: 0,
      quantityAllocated: 0,
      quantityStaged: 0,
      quantityInstalled: 0,
      missingItems: 0,
      fieldPurchase: false,
      inventoryAssignment: "Stock",
      lifecycleStage: "Ordered",
      inventoryStatus: "In Stock",
    };
    bySku.set(skuKey, next);
    return next;
  };

  for (const raw of existingRows) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const target = ensure(row.sku, row.itemName);
    if (!target) continue;
    target.itemName = String(row.itemName ?? target.itemName).trim() || target.itemName;
    target.quantityRequired = Math.max(target.quantityRequired, Math.max(0, toNumber(row.quantityRequired)));
    target.quantityAllocated = Math.max(target.quantityAllocated, Math.max(0, toNumber(row.quantityAllocated)));
    target.quantityStaged = Math.max(target.quantityStaged, Math.max(0, toNumber(row.quantityStaged)));
    target.quantityInstalled = Math.max(target.quantityInstalled, Math.max(0, toNumber(row.quantityInstalled)));
    target.fieldPurchase = target.fieldPurchase || Boolean(row.fieldPurchase);
    target.inventoryAssignment = row.inventoryAssignment === "Assigned" ? "Assigned" : target.inventoryAssignment;
  }

  for (const row of jobMaterialsRows) {
    const target = ensure(row.sku, row.item_name);
    if (!target) continue;
    const skuKey = normalizeSkuKey(target.sku);
    if (skuKey) skusFromJobMaterials.add(skuKey);
    target.itemName = String(row.item_name ?? target.itemName).trim() || target.itemName;
    target.quantityRequired = Math.max(0, toNumber(row.quantity_required));
    target.quantityAllocated = Math.max(target.quantityAllocated, Math.max(0, toNumber(row.quantity_allocated)));
    target.quantityStaged = Math.max(target.quantityStaged, Math.max(0, toNumber(row.quantity_staged)));
    target.quantityInstalled = Math.max(target.quantityInstalled, Math.max(0, toNumber(row.quantity_installed)));
    target.fieldPurchase = target.fieldPurchase || Boolean(row.field_purchase);
    target.inventoryAssignment = row.inventory_assignment === "Assigned" ? "Assigned" : target.inventoryAssignment;
  }

  const allocBySku = new Map<string, number>();
  for (const row of allocationRows) {
    if (row.status !== "Reserved" && row.status !== "Ready for Install") continue;
    const target = ensure(row.sku, row.product_name);
    if (!target) continue;
    const key = normalizeSkuKey(target.sku);
    allocBySku.set(key, (allocBySku.get(key) ?? 0) + Math.max(0, toNumber(row.quantity)));
  }

  const receivedBySku = new Map<string, { expected: number; received: number }>();
  for (const row of receivingRows) {
    const target = ensure(row.sku, row.product_name);
    if (!target) continue;
    const key = normalizeSkuKey(target.sku);
    const current = receivedBySku.get(key) ?? { expected: 0, received: 0 };
    current.expected += Math.max(0, toNumber(row.expected_quantity));
    current.received += Math.max(0, toNumber(row.received_quantity));
    receivedBySku.set(key, current);
  }

  const nextRows: CanonicalMaterialSummaryRow[] = Array.from(bySku.entries()).map(([key, row]) => {
    const allocationQty = allocBySku.get(key) ?? 0;
    const receiving = receivedBySku.get(key);
    const hasAuthoritativeRow = skusFromJobMaterials.has(key);
    const required = hasAuthoritativeRow
      ? Math.max(row.quantityRequired, allocationQty > 0 ? 1 : 0)
      : Math.max(row.quantityRequired, receiving?.expected ?? 0, allocationQty > 0 ? 1 : 0);
    const allocated = Math.max(0, allocationQty);
    const staged = Math.max(0, row.quantityStaged);
    const installed = Math.max(0, row.quantityInstalled);
    const missing = Math.max(0, required - allocated);
    const lifecycleStage: CanonicalMaterialSummaryRow["lifecycleStage"] =
      installed > 0 ? "Installed" : staged > 0 ? "Assigned" : allocated > 0 || (receiving?.received ?? 0) > 0 ? "Received" : "Ordered";
    const inventoryStatus: CanonicalMaterialSummaryRow["inventoryStatus"] =
      missing > 0 ? "Low Stock" : allocated > 0 ? "Allocated" : "In Stock";
    return {
      ...row,
      quantityRequired: required,
      quantityAllocated: allocated,
      quantityStaged: staged,
      quantityInstalled: installed,
      missingItems: missing,
      lifecycleStage,
      inventoryStatus,
    };
  });

  const rowsForSummary = terminalReadiness
    ? nextRows.map((row) => ({
      ...row,
      missingItems: 0,
      fieldPurchase: false,
    }))
    : nextRows;

  const missingItemsList = rowsForSummary
    .filter((row) => row.missingItems > 0)
    .map((row) => row.itemName)
    .join(", ");

  const missingCount = rowsForSummary.filter((row) => row.missingItems > 0).length;
  const summarySource = terminalReadiness ? "job_materials_terminal_readiness" : "job_materials";
  const summaryHash = `ml_${rowsForSummary.length}_${rowsForSummary.reduce(
    (acc, row) => acc + row.quantityRequired + row.quantityAllocated + row.missingItems,
    0,
  )}`;
  const currentSummary = metadata.materialsSummary && typeof metadata.materialsSummary === "object"
    ? (metadata.materialsSummary as Record<string, unknown>)
    : {};

  const unchanged =
    String(metadata.missingItemsList ?? "") === missingItemsList &&
    String(currentSummary.source ?? "") === summarySource &&
    Number(currentSummary.itemCount ?? -1) === rowsForSummary.length &&
    Number(currentSummary.missingCount ?? -1) === missingCount &&
    String(currentSummary.hash ?? "") === summaryHash;

  if (unchanged) return null;

  return {
    ...metadata,
    missingItemsList,
    materialsSummary: {
      source: summarySource,
      itemCount: rowsForSummary.length,
      missingCount,
      hash: summaryHash,
      updatedAt: new Date().toISOString(),
    },
  };
}

/**
 * Ensures job_materials contains the current snapshot for a given job.
 * This keeps warehouse/install-readiness views in sync with the sales-side material list.
 */
export async function replaceJobMaterialsSnapshot(
  job: Pick<Job, "id" | "jobNumber" | "customerName" | "currentStage" | "materialsList"> & { projectType?: string },
): Promise<void> {
  const {
    canUseProductId,
    canUsePricesSnapshot,
    canUseUnitCostSnapshot,
    canUseUnitPriceSnapshot,
  } = await getJobMaterialsSnapshotSupport();
  const skuToProductId = canUseProductId ? await getSkuToProductIdMap() : new Map<string, string>();

  // Fetch existing warehouse-tracked state before wiping the snapshot so that
  // delivery-driven or manually-set allocations survive a job-detail edit.
  // The sales side (quantity_required, item_name) comes from materialsList;
  // the warehouse side (quantity_allocated, staged, installed, lifecycle) is
  // preserved from the live job_materials rows.
  const existingWarehouseState = new Map<string, {
    quantity_allocated: number;
    quantity_staged: number;
    quantity_installed: number;
    lifecycle_stage: string;
    inventory_status: string;
  }>();
  // Also capture full row data so we can re-insert active ordering rows
  // (e.g. "Additional Soap Wall") that were added during ordering and are
  // NOT in materialsList — without this they get wiped by the DELETE below.
  let existingMaterialRowsFull: Array<{
    sku: string;
    item_name: string;
    quantity_required: number;
    quantity_allocated: number;
    quantity_staged: number;
    quantity_installed: number;
    lifecycle_stage: string;
    inventory_status: string;
    field_purchase: boolean;
    inventory_assignment: string;
  }> = [];
  {
    const jobIdForLookup = String(job.id || "").trim();
    if (jobIdForLookup) {
      const { data: existingMaterialRows } = await supabase
        .from("job_materials")
        .select("sku, item_name, quantity_required, quantity_allocated, quantity_staged, quantity_installed, lifecycle_stage, inventory_status, field_purchase, inventory_assignment")
        .eq("job_id", jobIdForLookup);
      existingMaterialRowsFull = existingMaterialRows ?? [];
      for (const row of existingMaterialRowsFull) {
        const key = normalizeSkuKey(String(row.sku ?? "").trim());
        if (!key) continue;
        existingWarehouseState.set(key, {
          quantity_allocated: Math.max(0, Number(row.quantity_allocated ?? 0)),
          quantity_staged: Math.max(0, Number(row.quantity_staged ?? 0)),
          quantity_installed: Math.max(0, Number(row.quantity_installed ?? 0)),
          lifecycle_stage: String(row.lifecycle_stage ?? "Ordered"),
          inventory_status: String(row.inventory_status ?? "In Stock"),
        });
      }
    }
  }

  const organizationId = await getCurrentOrganizationId();
  const rawRows = (job.materialsList ?? [])
    .filter((item) => !!item?.sku)
    .map((item) => {
      const sku = String(item.sku || "").trim();
      const productId = skuToProductId.get(normalizeSkuKey(sku)) || null;
      const skuKey = normalizeSkuKey(sku);
      const existing = skuKey ? existingWarehouseState.get(skuKey) : undefined;
      const qRequired = Math.max(0, Number(item.quantityRequired ?? 0));
      // Preserve warehouse-tracked allocations; incoming materialsList.quantityAllocated
      // may be stale (e.g. loaded before a delivery-driven reconcile ran in another tab).
      const warehouseAllocated = (existing && existing.quantity_allocated > 0)
        ? (qRequired > 0 ? Math.min(existing.quantity_allocated, qRequired) : existing.quantity_allocated)
        : Math.max(0, Number(item.quantityAllocated ?? 0));
      const warehouseStaged = existing
        ? Math.min(existing.quantity_staged, warehouseAllocated)
        : Math.max(0, Number(item.quantityStaged ?? 0));
      const warehouseInstalled = existing?.quantity_installed ?? Math.max(0, Number(item.quantityInstalled ?? 0));
      const warehouseMissing = Math.max(0, qRequired - warehouseAllocated);
      const warehouseLifecycle = (
        warehouseInstalled > 0 ? "Installed" :
        warehouseStaged > 0 ? "Assigned" :
        warehouseAllocated > 0 ? "Received" :
        normalizeLifecycle(item.lifecycleStage)
      ) as "Ordered" | "Received" | "Assigned" | "Installed";
      const warehouseInventoryStatus = (
        warehouseMissing > 0 ? "Low Stock" :
        warehouseAllocated > 0 ? "Allocated" :
        normalizeInventoryStatus(item.inventoryStatus)
      ) as "In Stock" | "Reserved" | "Allocated" | "Low Stock";
      const jobName = job.customerName
        ? `${job.customerName} – ${job.projectType || "Project"}`
        : job.jobNumber || job.id;
      return {
      job_id: job.id,
      job_name: jobName,
      customer_name: job.customerName || null,
      current_stage: job.currentStage || "Sale Closed",
      organization_id: organizationId,
      ...(canUseProductId ? { product_id: productId } : {}),
      ...(canUsePricesSnapshot ? { prices_snapshot: Number(item.unitCost) || 0 } : {}),
      ...(canUseUnitCostSnapshot ? { unit_cost_snapshot: Number(item.unitCost) || 0 } : {}),
      ...(canUseUnitPriceSnapshot ? { unit_price_snapshot: Number(item.unitPrice) || 0 } : {}),
      sku,
      item_name: item.itemName || item.sku,
      quantity_required: qRequired,
      quantity_allocated: warehouseAllocated,
      quantity_staged: warehouseStaged,
      quantity_installed: warehouseInstalled,
      missing_items: warehouseMissing,
      field_purchase: Boolean(item.fieldPurchase),
      inventory_assignment: warehouseAllocated > 0 ? "Assigned" as const : normalizeAssignment(item.inventoryAssignment),
      lifecycle_stage: warehouseLifecycle,
      inventory_status: warehouseInventoryStatus,
    };});

  // Deduplicate by the exact upsert conflict key (job_id, sku).
  // Postgres rejects upsert batches where the same conflict key appears multiple times.
  const lifecycleRank: Record<"Ordered" | "Received" | "Assigned" | "Installed", number> = {
    Ordered: 0,
    Received: 1,
    Assigned: 2,
    Installed: 3,
  };
  const inventoryStatusRank: Record<"In Stock" | "Reserved" | "Allocated" | "Low Stock", number> = {
    "In Stock": 0,
    Reserved: 1,
    Allocated: 2,
    "Low Stock": 3,
  };

  const mergedByKey = new Map<string, (typeof rawRows)[number]>();
  for (const row of rawRows) {
    const key = `${row.job_id}::${row.sku}`;
    const existing = mergedByKey.get(key);
    if (!existing) {
      mergedByKey.set(key, { ...row });
      continue;
    }

    existing.quantity_required += row.quantity_required;
    existing.quantity_allocated += row.quantity_allocated;
    existing.quantity_staged += row.quantity_staged;
    existing.quantity_installed += row.quantity_installed;
    existing.missing_items += row.missing_items;
    existing.field_purchase = existing.field_purchase || row.field_purchase;
    if (row.inventory_assignment === "Assigned") {
      existing.inventory_assignment = "Assigned";
    }
    if (lifecycleRank[row.lifecycle_stage] > lifecycleRank[existing.lifecycle_stage]) {
      existing.lifecycle_stage = row.lifecycle_stage;
    }
    if (inventoryStatusRank[row.inventory_status] > inventoryStatusRank[existing.inventory_status]) {
      existing.inventory_status = row.inventory_status;
    }
  }

  // Preserve active ordering rows not in materialsList.
  // These are materials added during the ordering process (e.g. "Additional Soap Wall")
  // that have already been received, allocated, or staged. Without this, the DELETE
  // below would silently wipe them because they aren't in materialsList.
  const jobName = job.customerName
    ? `${job.customerName} – ${job.projectType || "Project"}`
    : job.jobNumber || job.id;
  for (const row of existingMaterialRowsFull) {
    const sku = String(row.sku ?? "").trim();
    if (!sku) continue;
    const key = `${job.id}::${sku}`;
    if (mergedByKey.has(key)) continue; // already covered by materialsList
    // Only preserve rows that have been physically staged or installed.
    // Allocation alone (quantity_allocated > 0) is not enough to preserve a row —
    // the lifecycle_stage "Received" is derived from allocation, not physical receipt,
    // so checking lifecycle_stage would also wrongly preserve allocated-only rows.
    // This allows intentional editor deletions to take effect even for allocated SKUs.
    const hasActivity =
      Number(row.quantity_staged ?? 0) > 0 ||
      Number(row.quantity_installed ?? 0) > 0;
    if (!hasActivity) continue; // not staged or installed — safe to drop
    mergedByKey.set(key, {
      job_id: job.id,
      job_name: jobName,
      customer_name: job.customerName || null,
      current_stage: job.currentStage || "Sale Closed",
      organization_id: organizationId,
      sku,
      item_name: String(row.item_name || sku),
      quantity_required: Math.max(0, Number(row.quantity_required ?? 0)),
      quantity_allocated: Math.max(0, Number(row.quantity_allocated ?? 0)),
      quantity_staged: Math.max(0, Number(row.quantity_staged ?? 0)),
      quantity_installed: Math.max(0, Number(row.quantity_installed ?? 0)),
      missing_items: Math.max(0, Number(row.quantity_required ?? 0) - Number(row.quantity_allocated ?? 0)),
      field_purchase: Boolean(row.field_purchase),
      inventory_assignment: String(row.inventory_assignment || "Stock") as "Stock" | "Assigned",
      lifecycle_stage: String(row.lifecycle_stage || "Ordered") as "Ordered" | "Received" | "Assigned" | "Installed",
      inventory_status: String(row.inventory_status || "In Stock") as "In Stock" | "Reserved" | "Allocated" | "Low Stock",
    });
  }

  const rows = Array.from(mergedByKey.values());
  const jobId = String(job.id || "").trim();
  if (!jobId) return;

  // Delete the existing snapshot first so stale/orphaned SKU rows don't persist
  // when a material is removed or its SKU changes (e.g. placeholder → catalog code).
  const { error: deleteError } = await supabase
    .from("job_materials")
    .delete()
    .eq("job_id", jobId);

  if (deleteError) {
    console.error("[inventory.service] Failed to delete stale job_materials before snapshot:", deleteError);
    // Fallback: upsert to avoid losing data entirely
    const { error: upsertError } = await supabase
      .from("job_materials")
      .upsert(rows, { onConflict: "job_id,sku" });
    if (upsertError) {
      console.error("[inventory.service] Failed to upsert job_materials snapshot:", upsertError);
    }
    return;
  }

  if (rows.length === 0) {
    return;
  }

  const { error } = await supabase
    .from("job_materials")
    .insert(rows);

  if (error) {
    console.error("[inventory.service] Failed to insert job_materials snapshot:", error);
  }

  void SystemUpdateService.post({
    domain: 'inventory',
    event:  'stock_allocated',
    body:   `System: Stock Allocated to Job — ${job.jobNumber || jobId}`,
  });
}

/**
 * Fetch job materials from Supabase
 * Returns jobs that have materials assigned
 */
export async function fetchJobMaterials(): Promise<JobWithMaterials[]> {
  let materials: any[] | null = null;
  let error: any = null;

  // Prefer canonical table first.
  const primary = await supabase
    .from("job_materials")
    .select("*")
    .order("job_id", { ascending: true });

  if (!primary.error) {
    materials = primary.data;
  } else if (primary.error.code !== "PGRST116") {
    error = primary.error;
  }

  // Fallback: legacy "materials" table when canonical table is unavailable/empty.
  if (!materials || materials.length === 0) {
    const fallback = await supabase.from("materials").select("*");
    if (!fallback.error) {
      materials = fallback.data;
      error = null;
    } else if (fallback.error.code !== "PGRST116") {
      error = fallback.error;
    }
  }

  if (error) {
    console.error("Error fetching job materials:", error);
    if (error.code === "PGRST116" || error.code === "42703") return [];
    throw error;
  }

  if (!materials || materials.length === 0) {
    return [];
  }

  // Group materials by job_id
  const jobMaterialsMap = new Map<string, JobMaterialRecord[]>();
  
  for (const mat of materials) {
    const jobId = mat.job_id ?? mat.jobId;
    const sku = mat.sku ?? mat.item_sku;
    if (!jobId || !sku) continue;

    const quantityRequired = Number(mat.quantity_required ?? mat.quantityRequired ?? 0);
    const quantityAllocated = Number(mat.quantity_allocated ?? mat.quantityAllocated ?? 0);
    const quantityStaged = Number(mat.quantity_staged ?? mat.quantityStaged ?? 0);
    const quantityInstalled = Number(mat.quantity_installed ?? mat.quantityInstalled ?? 0);
    const missingItems = Number(mat.missing_items ?? mat.missingItems ?? Math.max(0, quantityRequired - quantityStaged));

    const record: JobMaterialRecord = {
      id: mat.id,
      jobId,
      sku,
      itemName: mat.item_name ?? mat.itemName ?? sku,
      quantityRequired,
      quantityAllocated,
      quantityStaged,
      quantityInstalled,
      missingItems,
      fieldPurchase: Boolean(mat.field_purchase ?? mat.fieldPurchase),
      inventoryAssignment: mat.inventory_assignment || "Stock",
      lifecycleStage: mat.lifecycle_stage || "Ordered",
      inventoryStatus: mat.inventory_status || "In Stock",
      customerName: mat.customer_name || mat.customerName || `Job ${jobId}`,
    };

    if (!jobMaterialsMap.has(jobId)) {
      jobMaterialsMap.set(jobId, []);
    }
    jobMaterialsMap.get(jobId)!.push(record);
  }

  // Build result with job info from job_materials table
  return Array.from(jobMaterialsMap.entries()).map(([jobId, materials]) => {
    // Get job info from first material record
    const firstMat = materials[0];
    return {
      id: jobId,
      customerName: firstMat?.customerName || `Job ${jobId}`,
      currentStage: firstMat?.lifecycleStage || "In Progress",
      materialsList: materials,
    };
  });
}

async function fetchInventoryItemName(sku: string): Promise<string> {
  const normalizedSku = String(sku || "").trim();
  if (!normalizedSku) return "";
  const cached = inventoryItemNameCache.get(normalizedSku);
  if (cached) return cached;
  const inFlight = inventoryItemNameInFlight.get(normalizedSku);
  if (inFlight) return inFlight;

  const promise = (async () => {
    const { data } = await supabase
      .from("inventory_items")
      .select("item_name")
      .eq("sku", normalizedSku)
      .maybeSingle();

    const resolved = data?.item_name || normalizedSku;
    inventoryItemNameCache.set(normalizedSku, resolved);
    if (data?.item_name) {
      inventoryItemExistsCache.add(normalizedSku);
    }
    return resolved;
  })();

  inventoryItemNameInFlight.set(normalizedSku, promise);
  try {
    return await promise;
  } finally {
    inventoryItemNameInFlight.delete(normalizedSku);
  }
}

async function ensureInventoryItemExists(sku: string, itemName?: string): Promise<void> {
  const normalizedSku = String(sku || "").trim();
  if (!normalizedSku) return;
  if (inventoryItemExistsCache.has(normalizedSku)) return;
  const inFlight = inventoryItemExistsInFlight.get(normalizedSku);
  if (inFlight) return inFlight;

  const promise = (async () => {
    const { data: existing, error: lookupError } = await supabase
      .from("inventory_items")
      .select("sku")
      .eq("sku", normalizedSku)
      .maybeSingle();

    if (lookupError) {
      console.error("[inventory.service] Failed to lookup inventory item before stock sync:", lookupError);
      return;
    }
    if (existing?.sku) {
      inventoryItemExistsCache.add(normalizedSku);
      return;
    }

    const resolvedItemName = (itemName || normalizedSku).trim();
    const organizationId = await getCurrentOrganizationId();
    if (!organizationId) {
      console.warn("[inventory.service] ensureInventoryItemExists: no organization_id, skipping upsert for SKU:", normalizedSku);
      return;
    }
    const { error: upsertError } = await supabase
      .from("inventory_items")
      .upsert(
        {
          sku: normalizedSku,
          item_name: resolvedItemName,
          category: "other",
          vendor: "Unassigned",
          unit_of_measure: "each",
          cost: 0,
          price: 0,
          is_active: true,
        },
        { onConflict: "sku" },
      );

    if (upsertError) {
      console.error("[inventory.service] Failed to seed missing inventory_items row for SKU:", normalizedSku, upsertError);
      return;
    }

    inventoryItemExistsCache.add(normalizedSku);
    inventoryItemNameCache.set(normalizedSku, resolvedItemName);
  })();

  inventoryItemExistsInFlight.set(normalizedSku, promise);
  try {
    await promise;
  } finally {
    inventoryItemExistsInFlight.delete(normalizedSku);
  }
}

async function getStockLevelRecord(sku: string): Promise<StockLevelRecord | null> {
  const normalizedSku = String(sku || "").trim();
  if (!normalizedSku) return null;
  const inFlight = stockLevelRecordInFlight.get(normalizedSku);
  if (inFlight) return inFlight;

  const promise = (async () => {
    const { data, error } = await supabase
      .from("stock_levels")
      .select("*")
      .eq("sku", normalizedSku)
      .maybeSingle();

    if (error) {
      console.error("[inventory.service] Failed to fetch stock level record:", error);
      return null;
    }

    return data as StockLevelRecord | null;
  })();

  stockLevelRecordInFlight.set(normalizedSku, promise);
  try {
    return await promise;
  } finally {
    stockLevelRecordInFlight.delete(normalizedSku);
  }
}

async function getWarehouseStockRecord(sku: string): Promise<WarehouseStockRecord | null> {
  const normalizedSku = String(sku || "").trim();
  if (!normalizedSku) return null;
  const inFlight = warehouseStockRecordInFlight.get(normalizedSku);
  if (inFlight) return inFlight;

  const promise = (async () => {
    const { data, error } = await supabase
      .from("warehouse_stock")
      .select("*")
      .eq("sku", normalizedSku)
      .maybeSingle();

    if (error) {
      console.error("[inventory.service] Failed to fetch warehouse stock record:", error);
      return null;
    }

    return data as WarehouseStockRecord | null;
  })();

  warehouseStockRecordInFlight.set(normalizedSku, promise);
  try {
    return await promise;
  } finally {
    warehouseStockRecordInFlight.delete(normalizedSku);
  }
}

export async function syncStockLevels(
  sku: string,
  options: StockMutationOptions,
): Promise<void> {
  const normalizedSku = String(sku || "").trim();
  if (!normalizedSku) return;
  const syncKey = `${normalizedSku}|${getStockMutationSignature(options)}`;

  await withStockSyncDeduplication(stockLevelSyncInFlight, syncKey, async () => {
    await ensureInventoryItemExists(normalizedSku);
    // If the inventory_items row still doesn't exist (upsert failed, e.g. RLS/org_id),
    // skip the stock_levels write — it would violate the FK constraint otherwise.
    if (!inventoryItemExistsCache.has(normalizedSku)) {
      console.warn("[inventory.service] Skipping stock_levels sync — inventory_items row unavailable for SKU:", normalizedSku);
      return;
    }
    const existing = await getStockLevelRecord(normalizedSku);
    const quantityOnHand = Math.max(0, (existing?.quantity_on_hand ?? 0) + (options.quantityOnHandDelta ?? 0));
    const quantityReserved = Math.max(0, (existing?.quantity_reserved ?? 0) + (options.quantityReservedDelta ?? 0));
    const reorderPoint = existing?.reorder_point ?? 0;
    const available = Math.max(0, quantityOnHand - quantityReserved);
    const status = available <= 0 ? "Out of Stock" : available <= reorderPoint ? "Low Stock" : "In Stock";

    const { error } = await supabase
      .from("stock_levels")
      .upsert({
        id: existing?.id,
        sku: normalizedSku,
        quantity_on_hand: quantityOnHand,
        quantity_reserved: quantityReserved,
        reorder_point: reorderPoint,
        status,
        last_synced_at: new Date().toISOString(),
        source: existing?.source || "simulated",
      }, { onConflict: "sku" });

    if (error) {
      console.error("[inventory.service] Failed to sync stock levels:", error);
      return;
    }

    stockLevelRecordInFlight.delete(normalizedSku);
    stockLevelCache.delete(normalizedSku);
    inventoryListCache = null;
  });
}

export async function syncWarehouseStock(
  sku: string,
  options: StockMutationOptions,
): Promise<void> {
  const normalizedSku = String(sku || "").trim();
  if (!normalizedSku) return;
  const syncKey = `${normalizedSku}|${getStockMutationSignature(options)}`;

  await withStockSyncDeduplication(warehouseStockSyncInFlight, syncKey, async () => {
    await ensureInventoryItemExists(normalizedSku);
    if (!inventoryItemExistsCache.has(normalizedSku)) {
      console.warn("[inventory.service] Skipping warehouse_stock sync — inventory_items row unavailable for SKU:", normalizedSku);
      return;
    }
    const [existing, productName] = await Promise.all([
      getWarehouseStockRecord(normalizedSku),
      fetchInventoryItemName(normalizedSku),
    ]);

    const currentStock = Math.max(0, (existing?.current_stock ?? 0) + (options.quantityOnHandDelta ?? 0));
    const reservedStock = Math.max(0, (existing?.reserved_stock ?? 0) + (options.quantityReservedDelta ?? 0));
    const incomingStock = Math.max(0, (existing?.incoming_stock ?? 0) + (options.incomingStockDelta ?? 0));
    const reorderThreshold = existing?.reorder_threshold ?? 0;
    const availableStock = Math.max(0, currentStock - reservedStock);

    const { error } = await supabase
      .from("warehouse_stock")
      .upsert({
        id: existing?.id,
        sku: normalizedSku,
        product_name: existing?.product_name || productName,
        current_stock: currentStock,
        reserved_stock: reservedStock,
        incoming_stock: incomingStock,
        available_stock: availableStock,
        reorder_threshold: reorderThreshold,
      }, { onConflict: "sku" });

    if (error) {
      console.error("[inventory.service] Failed to sync warehouse stock:", error);
      return;
    }

    warehouseStockRecordInFlight.delete(normalizedSku);
    inventoryItemExistsCache.add(normalizedSku);
    inventoryItemNameCache.set(normalizedSku, existing?.product_name || productName);
  });
}

export async function syncProductRegistryStock(
  sku: string,
  options: Pick<StockMutationOptions, "quantityOnHandDelta">,
): Promise<void> {
  const normalizedSku = String(sku || "").trim();
  if (!normalizedSku) return;

  const quantityDelta = Number(options.quantityOnHandDelta ?? 0);
  if (!Number.isFinite(quantityDelta) || quantityDelta === 0) return;

  const { data: existing, error: fetchError } = await supabase
    .from("product_registry")
    .select("stock_quantity")
    .eq("sku", normalizedSku)
    .maybeSingle();

  if (fetchError) {
    console.error("[inventory.service] Failed to fetch product registry stock:", fetchError);
    return;
  }

  if (!existing) {
    console.warn(`[inventory.service] Product registry row missing for SKU "${normalizedSku}" while syncing stock.`);
    return;
  }

  const nextStockQuantity = Math.max(0, Number(existing.stock_quantity ?? 0) + quantityDelta);
  const { error: updateError } = await supabase
    .from("product_registry")
    .update({
      stock_quantity: nextStockQuantity,
      track_inventory: true,
      updated_at: new Date().toISOString(),
    })
    .eq("sku", normalizedSku);

  if (updateError) {
    console.error("[inventory.service] Failed to sync product registry stock:", updateError);
  }
}

export async function syncJobMaterialState(
  jobId: string,
  sku: string,
  options: JobMaterialMutationOptions,
): Promise<void> {
  const syncJobMetadataMaterialSummary = async () => {
    const { data: jobRow, error: jobError } = await supabase
      .from("jobs")
      .select("id, metadata, material_readiness")
      .eq("id", jobId)
      .maybeSingle();

    if (jobError) {
      console.error("[inventory.service] Failed to fetch job metadata for material sync:", jobError);
      return;
    }

    const metadata = (jobRow?.metadata && typeof jobRow.metadata === "object")
      ? (jobRow.metadata as Record<string, unknown>)
      : {};
    const { data: materialRows, error: materialError } = await supabase
      .from("job_materials")
      .select("sku, item_name, quantity_required, missing_items")
      .eq("job_id", jobId);

    if (materialError) {
      console.error("[inventory.service] Failed to fetch job_materials for metadata summary sync:", materialError);
      return;
    }

    const rows = materialRows ?? [];
    const terminalReadiness = ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(
      String((jobRow as { material_readiness?: unknown } | null)?.material_readiness ?? "").trim(),
    );
    const rowsForSummary = terminalReadiness
      ? rows.map((row) => ({ ...row, missing_items: 0 }))
      : rows;
    const missingItemsList = rowsForSummary
      .filter((row) => Math.max(0, Number(row.missing_items ?? 0)) > 0)
      .map((row) => String(row.item_name ?? row.sku ?? "").trim())
      .filter(Boolean)
      .join(", ");

    const summaryToken = rowsForSummary
      .map((row) => [
        String(row.sku ?? ""),
        Math.max(0, Number(row.quantity_required ?? 0)),
        Math.max(0, Number(row.missing_items ?? 0)),
      ].join(":"))
      .sort()
      .join("|");

    const { error: updateJobError } = await supabase
      .from("jobs")
      .update({
        metadata: {
          ...metadata,
          missingItemsList,
          materialsSummary: {
            source: terminalReadiness ? "job_materials_terminal_readiness" : "job_materials",
            itemCount: rowsForSummary.length,
            missingCount: rowsForSummary.filter((row) => Math.max(0, Number(row.missing_items ?? 0)) > 0).length,
            hash: `ml_${summaryToken.length}_${rowsForSummary.length}`,
            updatedAt: new Date().toISOString(),
          },
        },
      })
      .eq("id", jobId);

    if (updateJobError) {
      console.error("[inventory.service] Failed to sync job metadata material summary:", updateJobError);
    }
  };

  const allocatedDelta = options.quantityAllocatedDelta ?? 0;
  const stagedDelta = options.quantityStagedDelta ?? 0;
  const installedDelta = options.quantityInstalledDelta ?? 0;

  const { data, error } = await supabase
    .from("job_materials")
    .select("*")
    .eq("job_id", jobId)
    .eq("sku", sku)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("[inventory.service] Failed to fetch job material row:", error);
    await syncJobMetadataMaterialSummary();
    return;
  }

  if (!data) {
    await syncJobMetadataMaterialSummary();
    return;
  }

  const nextAllocated = Math.max(0, (data.quantity_allocated ?? 0) + allocatedDelta);
  const nextStaged = Math.max(0, (data.quantity_staged ?? 0) + stagedDelta);
  const nextInstalled = Math.max(0, (data.quantity_installed ?? 0) + installedDelta);
  const quantityRequired = Math.max(0, Number(data.quantity_required ?? 0));
  const nextMissing = Math.max(0, quantityRequired - nextAllocated);

  const { error: updateError } = await supabase
    .from("job_materials")
    .update({
      quantity_allocated: nextAllocated,
      quantity_staged: nextStaged,
      quantity_installed: nextInstalled,
      missing_items: nextMissing,
      lifecycle_stage: options.lifecycleStage ?? data.lifecycle_stage,
      inventory_status: options.inventoryStatus ?? data.inventory_status,
    })
    .eq("id", data.id);

  if (updateError) {
    console.error("[inventory.service] Failed to update job material row:", updateError);
  }

  await syncJobMetadataMaterialSummary();
}

export async function rebuildJobMaterialsList(jobId: string): Promise<boolean> {
  const normalizedJobId = String(jobId || "").trim();
  if (!normalizedJobId) return false;

  const inFlight = rebuildJobMaterialsInFlight.get(normalizedJobId);
  if (inFlight) return inFlight;

  const run = (async () => {
  if (import.meta.env.DEV) {
    console.debug("[inventory.service] rebuildJobMaterialsList", { jobId: normalizedJobId });
  }

  const [jobRes, jobMaterialsRes, allocationsRes, receivingRes] = await Promise.all([
    supabase
      .from("jobs")
      .select("id, metadata, material_readiness")
      .eq("id", normalizedJobId)
      .maybeSingle(),
    supabase
      .from("job_materials")
      .select("job_id, sku, item_name, quantity_required, quantity_allocated, quantity_staged, quantity_installed, field_purchase, inventory_assignment, lifecycle_stage, inventory_status")
      .eq("job_id", normalizedJobId),
    supabase
      .from("job_allocations")
      .select("job_id, sku, product_name, quantity, status")
      .eq("job_id", normalizedJobId),
    supabase
      .from("receiving_records")
      .select("job_id, sku, product_name, expected_quantity, received_quantity, status")
      .eq("job_id", normalizedJobId),
  ]);

  if (jobRes.error || !jobRes.data) {
    if (jobRes.error) console.error("[inventory.service] Failed to fetch job for rebuildJobMaterialsList:", jobRes.error);
    return false;
  }
  if (jobMaterialsRes.error) {
    console.error("[inventory.service] Failed to fetch job_materials for rebuildJobMaterialsList:", jobMaterialsRes.error);
    return false;
  }
  if (allocationsRes.error) {
    console.error("[inventory.service] Failed to fetch job_allocations for rebuildJobMaterialsList:", allocationsRes.error);
    return false;
  }
  if (receivingRes.error) {
    console.error("[inventory.service] Failed to fetch receiving_records for rebuildJobMaterialsList:", receivingRes.error);
    return false;
  }

  const nextMetadata = buildJobMaterialsMetadataPatch(
    jobRes.data as RebuildJobRow,
    (jobMaterialsRes.data ?? []) as RebuildJobMaterialRow[],
    (allocationsRes.data ?? []) as RebuildAllocationRow[],
    (receivingRes.data ?? []) as RebuildReceivingRow[],
  );

  if (!nextMetadata) return true;

  const { error: updateError } = await supabase
    .from("jobs")
    .update({ metadata: nextMetadata })
    .eq("id", normalizedJobId);

  if (updateError) {
    console.error("[inventory.service] Failed to update jobs.metadata in rebuildJobMaterialsList:", updateError);
    return false;
  }

  return true;
  })();

  rebuildJobMaterialsInFlight.set(normalizedJobId, run);
  try {
    return await run;
  } finally {
    rebuildJobMaterialsInFlight.delete(normalizedJobId);
  }
}

export async function backfillAllJobsMaterialsList(): Promise<{ total: number; updated: number; failed: number }> {
  if (backfillJobMaterialsInFlight) return backfillJobMaterialsInFlight;

  const run = (async () => {
  if (import.meta.env.DEV) {
    console.info("[inventory.service] backfillAllJobsMaterialsList started");
  }

  const { data, error } = await supabase.from("jobs").select("id, metadata, material_readiness");
  if (error) {
    console.error("[inventory.service] Failed to load jobs for backfillAllJobsMaterialsList:", error);
    return { total: 0, updated: 0, failed: 0 };
  }

  const jobs = (data ?? []) as RebuildJobRow[];
  let updated = 0;
  let failed = 0;

  const groupByJobId = <T extends { job_id?: unknown }>(rows: T[]): Map<string, T[]> => {
    const grouped = new Map<string, T[]>();
    for (const row of rows) {
      const jobId = String(row.job_id ?? "").trim();
      if (!jobId) continue;
      const bucket = grouped.get(jobId) ?? [];
      bucket.push(row);
      grouped.set(jobId, bucket);
    }
    return grouped;
  };

  for (let index = 0; index < jobs.length; index += JOB_MATERIALS_BACKFILL_CHUNK_SIZE) {
    const chunk = jobs.slice(index, index + JOB_MATERIALS_BACKFILL_CHUNK_SIZE);
    const jobIds = chunk.map((row) => String(row.id)).filter(Boolean);
    if (jobIds.length === 0) continue;

    const [jobMaterialsRes, allocationsRes, receivingRes] = await Promise.all([
      supabase
        .from("job_materials")
        .select("job_id, sku, item_name, quantity_required, quantity_allocated, quantity_staged, quantity_installed, field_purchase, inventory_assignment, lifecycle_stage, inventory_status")
        .in("job_id", jobIds),
      supabase
        .from("job_allocations")
        .select("job_id, sku, product_name, quantity, status")
        .in("job_id", jobIds),
      supabase
        .from("receiving_records")
        .select("job_id, sku, product_name, expected_quantity, received_quantity, status")
        .in("job_id", jobIds),
    ]);

    if (jobMaterialsRes.error || allocationsRes.error || receivingRes.error) {
      if (jobMaterialsRes.error) console.error("[inventory.service] Backfill job_materials chunk failed:", jobMaterialsRes.error);
      if (allocationsRes.error) console.error("[inventory.service] Backfill job_allocations chunk failed:", allocationsRes.error);
      if (receivingRes.error) console.error("[inventory.service] Backfill receiving_records chunk failed:", receivingRes.error);
      failed += chunk.length;
      continue;
    }

    const materialsByJob = groupByJobId((jobMaterialsRes.data ?? []) as RebuildJobMaterialRow[]);
    const allocationsByJob = groupByJobId((allocationsRes.data ?? []) as RebuildAllocationRow[]);
    const receivingByJob = groupByJobId((receivingRes.data ?? []) as RebuildReceivingRow[]);

    for (const row of chunk) {
      const jobId = String(row.id);
      const nextMetadata = buildJobMaterialsMetadataPatch(
        row,
        materialsByJob.get(jobId) ?? [],
        allocationsByJob.get(jobId) ?? [],
        receivingByJob.get(jobId) ?? [],
      );

      if (!nextMetadata) continue;

      const { error: updateError } = await supabase
        .from("jobs")
        .update({ metadata: nextMetadata })
        .eq("id", jobId);

      if (updateError) {
        console.error("[inventory.service] Failed to update jobs.metadata during materials backfill:", {
          jobId,
          error: updateError,
        });
        failed += 1;
        continue;
      }

      updated += 1;
    }
  }
  const result = { total: jobs.length, updated, failed };
  if (import.meta.env.DEV) {
    console.info("[inventory.service] backfillAllJobsMaterialsList finished", result);
  }
  return result;
  })();

  backfillJobMaterialsInFlight = run;
  try {
    return await run;
  } finally {
    backfillJobMaterialsInFlight = null;
  }
}

export async function markJobMaterialsInstalled(jobId: string): Promise<void> {
  const normalizedJobId = String(jobId || "").trim();
  if (!normalizedJobId) return;

  const { data, error } = await supabase
    .from("job_materials")
    .select("id, quantity_allocated, quantity_installed")
    .eq("job_id", normalizedJobId);

  if (error) {
    console.error("[inventory.service] Failed to fetch job materials for install sync:", error);
    return;
  }

  const rows = data ?? [];
  if (rows.length === 0) return;

  await Promise.all(
    rows.map(async (row) => {
      const allocated = Math.max(0, Number(row.quantity_allocated ?? 0));
      const installed = Math.max(0, Number(row.quantity_installed ?? 0));
      if (installed >= allocated) return;
      const { error: updateError } = await supabase
        .from("job_materials")
        .update({
          quantity_installed: allocated,
          lifecycle_stage: "Installed",
        })
        .eq("id", row.id);
      if (updateError) {
        console.error("[inventory.service] Failed to mark job material as installed:", updateError);
      }
    }),
  );

  await rebuildJobMaterialsList(normalizedJobId);

  // Resolve human-readable job label for the system update message
  const { data: jobLabelRow } = await supabase
    .from('jobs')
    .select('job_number')
    .eq('id', normalizedJobId)
    .maybeSingle();
  const installedJobLabel = String(jobLabelRow?.job_number ?? normalizedJobId);

  void SystemUpdateService.post({
    domain:   'inventory',
    event:    'materials_installed',
    body:     `System: Job Materials Marked Installed — ${installedJobLabel}`,
    metadata: {
      title:  'Materials Marked as Installed',
      detail: `Job ${installedJobLabel}`,
      action: { label: 'View Inventory', path: '/warehouse?tab=inventory' },
    },
  });
}

/**
 * Repair stock numbers for one or all SKUs by recomputing absolute values
 * from the source records (receiving_records + job_allocations) and writing
 * them into warehouse_stock, stock_levels, and product_registry.
 *
 * This is the only idempotent repair path — delta-based syncs accumulate
 * drift whenever they fail silently (missing inventory_items row, RLS error,
 * or double-application). Call this from the Numbers Audit panel to reset
 * a drifted SKU to the correct values.
 *
 * Source of truth:
 *   quantity_on_hand  = SUM(received_quantity) from Fully Received records
 *   quantity_installed = SUM(quantity) from Installed allocations
 *   current_stock     = MAX(0, quantity_on_hand - quantity_installed)
 *   quantity_reserved = SUM(quantity) from Reserved/Ready for Install allocations
 *   available_stock   = MAX(0, current_stock - quantity_reserved)
 */
export async function repairStockFromSourceRecords(
  sku: string,
): Promise<{ ok: boolean; corrected: { warehouseStock: boolean; stockLevels: boolean; productRegistry: boolean } }> {
  const normalizedSku = String(sku || "").trim();
  const result = { ok: false, corrected: { warehouseStock: false, stockLevels: false, productRegistry: false } };
  if (!normalizedSku) return result;

  // 1. Compute correct values from source records
  const [rrRes, allocRes, prNameRes] = await Promise.all([
    supabase
      .from("receiving_records")
      .select("received_quantity, product_name")
      .eq("sku", normalizedSku)
      .eq("status", "Fully Received"),
    supabase
      .from("job_allocations")
      .select("quantity, status")
      .eq("sku", normalizedSku)
      .in("status", ["Reserved", "Ready for Install", "Installed"]),
    supabase
      .from("product_registry")
      .select("name")
      .eq("sku", normalizedSku)
      .maybeSingle(),
  ]);

  if (rrRes.error || allocRes.error) {
    console.error("[inventory.service] repairStockFromSourceRecords: fetch failed", rrRes.error, allocRes.error);
    return result;
  }

  // product_name: prefer product_registry, fall back to first receiving record, then sku itself
  const productName =
    prNameRes.data?.name ||
    (rrRes.data ?? []).find((r) => r.product_name)?.product_name ||
    normalizedSku;

  const quantityOnHand = (rrRes.data ?? []).reduce((s, r) => s + Math.max(0, Number(r.received_quantity ?? 0)), 0);
  const quantityInstalled = (allocRes.data ?? [])
    .filter((r) => r.status === "Installed")
    .reduce((s, r) => s + Math.max(0, Number(r.quantity ?? 0)), 0);
  const quantityReserved = (allocRes.data ?? [])
    .filter((r) => r.status === "Reserved" || r.status === "Ready for Install")
    .reduce((s, r) => s + Math.max(0, Number(r.quantity ?? 0)), 0);

  const currentStock = Math.max(0, quantityOnHand - quantityInstalled);
  const availableStock = Math.max(0, currentStock - quantityReserved);

  // 2. Repair warehouse_stock (upsert absolute values)
  const wsRes = await supabase
    .from("warehouse_stock")
    .upsert(
      {
        sku: normalizedSku,
        product_name: productName,
        current_stock: currentStock,
        reserved_stock: quantityReserved,
        available_stock: availableStock,
        incoming_stock: 0, // reset incoming — it's fully received now
      },
      { onConflict: "sku" },
    );
  result.corrected.warehouseStock = !wsRes.error;
  if (wsRes.error) console.error("[inventory.service] repair warehouse_stock failed:", wsRes.error);

  // 3. Repair stock_levels (upsert absolute values)
  const slRes = await supabase
    .from("stock_levels")
    .upsert(
      {
        sku: normalizedSku,
        quantity_on_hand: quantityOnHand,
        quantity_reserved: quantityReserved,
        last_synced_at: new Date().toISOString(),
        source: "simulated",
      },
      { onConflict: "sku" },
    );
  result.corrected.stockLevels = !slRes.error;
  if (slRes.error) console.error("[inventory.service] repair stock_levels failed:", slRes.error);

  // 4. Repair product_registry if row exists (upsert absolute values)
  const { data: prRow } = await supabase
    .from("product_registry")
    .select("sku")
    .eq("sku", normalizedSku)
    .maybeSingle();
  if (prRow) {
    const prRes = await supabase
      .from("product_registry")
      .update({
        stock_quantity: currentStock,
        track_inventory: true,
        updated_at: new Date().toISOString(),
      })
      .eq("sku", normalizedSku);
    result.corrected.productRegistry = !prRes.error;
    if (prRes.error) console.error("[inventory.service] repair product_registry failed:", prRes.error);
  } else {
    // No product_registry row — not an error, just not catalogued yet
    result.corrected.productRegistry = true;
  }

  // Bust in-process caches so next read reflects repaired state
  stockLevelCache.delete(normalizedSku);
  stockLevelRecordInFlight.delete(normalizedSku);
  warehouseStockRecordInFlight.delete(normalizedSku);
  inventoryItemExistsCache.delete(normalizedSku);

  result.ok = result.corrected.warehouseStock && result.corrected.stockLevels;
  return result;
}
